class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('#shopify-section-header');
    this.docHtmlEl = document.querySelector('html');

    if (this.header) {
      this.inverse = !!this.header.classList.contains('header--inverse');
      this.shadow = this.header.querySelector('.header-shadow');

      this.headerCloseButtons = document.querySelectorAll('[data-header-navigation-close]');
      this.headerCloseButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeAll();
        });
      });
    }
  }

  construct() {
    // Init elements
    this.groups = this.hasAttribute('data-collapsible-group')
      ? [this.closest('[data-collapsible-group]')]
      : this.querySelectorAll('[data-collapsible-group]');

    // shadow events
    this.shadow?.addEventListener('click', this.closeAll.bind(this));

    // Click handler, only for touch devices or small screens
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-collapsible-trigger]') && (this.docHtmlEl.classList.contains('touch') || window.innerWidth < 1024)) {
        e.stopPropagation();
        this.debouncedOnMouse(e, 'toggle');
      }
    });

    // Trigger mouseleave on container <collapsible-element>
    if (this.options.closeOnMouseleave) {
      this.addEventListener('mouseleave', (e) => {
        e.stopPropagation();
        if (window.innerWidth < 1024) return;
        this.debouncedOnMouse(e, 'closeAll');
      });
    }

    this.nestedHoverEvents();
  }

  nestedHoverEvents() {
    if (this.options.onHover) {
      // Only add hover events on level-0 so the browser doesn't get overwhelmed by adding > 200 event listeners
      // Next level events will be added on the first hover event
      this.querySelectorAll('li.level-0 > [data-collapsible-trigger]').forEach((trigger0) => {
        // On the first hover event
        trigger0.addEventListener(
          'mouseenter',
          (e) => {
            this.querySelectorAll('.level-1[data-collapsible-trigger]').forEach((trigger1) => {
              // On the first hover event
              trigger1.addEventListener(
                'mouseenter',
                (e) => {
                  const subNav = trigger1.closest('[data-collapsible-group]').querySelector('[data-collapsible-target] ul');
                  if (!subNav) return;
                  // Add hover events to the second level
                  subNav.querySelectorAll('.level-2[data-collapsible-trigger]').forEach((el) => {
                    el.addEventListener('mouseenter', (e) => {
                      if (this.docHtmlEl.classList.contains('touch') || window.innerWidth < 1024) return;
                      // Open level-3 submenu
                      this.debouncedOnMouse(e, 'open');
                    });
                  });
                },
                { once: true }
              );
              // On each hover event
              trigger1.addEventListener('mouseenter', (e) => {
                if (this.docHtmlEl.classList.contains('touch') || window.innerWidth < 1024) return;
                // Open level-2 submenu
                this.debouncedOnMouse(e, 'open');
              });
            });
          },
          { once: true }
        );
        // On each hover event
        trigger0.addEventListener('mouseenter', (e) => {
          if (this.docHtmlEl.classList.contains('touch') || window.innerWidth < 1024) return;
          // Open level-2 submenu
          this.debouncedOnMouse(e, 'open');
        });
      });
    }
  }

  /*
    Debounced mouse event
    @param e {object}: mouseevent
    @param type {string}: open/close/closeAll
    Called by add event listener -> reducer.
  */
  debouncedOnMouse(e, type) {
    if (!e) {
      return false;
    }

    const trigger = e.target?.closest('[data-collapsible-trigger]');
    const group = trigger?.closest('[data-collapsible-group]');
    const target = group?.querySelector('[data-collapsible-target]');
    if (target) {
      e.preventDefault();
    }

    // clear old trigger
    clearTimeout(this.timeout);

    // add new trigger
    this.timeout = setTimeout(() => {
      switch (type) {
        case 'toggle':
          if (target) {
            this.toggle(group);
          }
          break;
        case 'open':
          this.open(group);
          break;
        case 'close':
          this.close(group);
          break;
        case 'closeAll':
          this.closeAll();
          break;
        default:
          break;
      }
    }, this.options.hoverDelay);
  }

  open(group) {
    super.open(group);
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
  }

  /*
    Debounce click event
    @param e {object}: mouseevent
    Called by add event listener -> reducer.
  */
  debounceClickEvent(e) {
    if (!e) {
      return false;
    }

    // clear old trigger
    clearTimeout(this.timeout);

    // find collapsible elements
    const trigger = e.target.closest('[data-collapsible-trigger]');
    const group = trigger.closest('[data-collapsible-group]');
    const target = group.querySelector('[data-collapsible-target]');
    if (!target) {
      return;
    }

    // Toggle collapsible
    this.timeout = setTimeout(() => {
      e.preventDefault();
      this.toggle(group);
    }, this.options.hoverDelay);
  }

  /*
    Toggle controller, open / close collapsibles
    @param group {node}: group selector
  */
  toggle(group) {
    if (!group) {
      return false;
    }

    // Check if already open
    if (!group.classList.contains(this.options.toggleClass)) {
      // Open
      this.open(group);
    } else {
      // Close
      this.close(group);
    }
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    if (open) {
      // set header open
      document.body.classList.add('header-is-open');

      this.inverse && this.header.classList.add('header-inverse-solid');
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        document.body.classList.remove('header-is-open');
        this.inverse && document.body.classList.remove('header-inverse-solid');
      }
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    document.querySelector('collapsible-header').closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('header-search-is-open');
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('header-search-is-open');
    }
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.headerElement = this;
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.header-wrapper--js');
    this.announcementBar = document.querySelector('.header-top--js');
    this.headerInverse = !!this.headerElement.classList.contains('header--inverse');
    this.headerNav = this.querySelector('collapsible-header');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.productInfo = document.querySelector('[data-product-info]');
    this.main = document.querySelector('main');

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.scrollOffset += announcementBarHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
      }

      // scroll event
      this.raf = null; // Safe request animation frame
      (this.timer = null), // Safe scroll timer
        window.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        );
    }
  }

  scrollAnimation() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    // only animate if header is closed.
    if (!document.body.classList.contains('header-is-open') && !document.body.classList.contains('predictive-search-is-open')) {
      if (scrollTop + window.innerHeight > document.body.clientHeight) {
        this.hide();
      } else if (scrollTop > this.currentScrollTop && scrollTop > this.scrollOffset) {
        this.hide();
      } else if (scrollTop <= this.headerStart) {
        this.revealTop();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  makeSticky() {
    document.body.classList.add('header-sticky');
    let headerHeight = this.headerElement.offsetHeight;
    this.main.style.paddingTop = headerHeight + 'px';
  }

  releaseSticky() {
    document.body.classList.remove('header-sticky');
    this.main.style.paddingTop = 0;
  }

  hide() {
    document.body.classList.add('header-hidden');
    this.makeSticky();
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    this.productInfoPosition('up');
  }

  reveal() {
    this.makeSticky();
    document.body.classList.remove('header-hidden');
    this.main.style.paddingTop = 0;
    if (this.headerInverse) {
      document.body.classList.add('header-inverse-solid');
    }
    this.productInfoPosition('down');
  }

  revealTop() {
    if (this.headerInverse && !document.body.classList.contains('header-is-open')) {
      document.body.classList.remove('header-inverse-solid');
    }
    document.body.classList.remove('header-hidden');
    this.releaseSticky();
    this.productInfoPosition('up');
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) {
      return false;
    }
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }

  productInfoPosition(pos = 'up') {
    if (!this.productInfo) {
      return;
    }
    if (pos == 'down') {
      this.productInfo.classList.replace('top-0', 'top-[4.5rem]');
    } else {
      this.productInfo.classList.replace('top-[4.5rem]', 'top-0');
    }
  }
}

if (!customElements.get('collapsible-header')) {
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
